import type { JSONSchemaProperty, ToolHandler } from 'app-components/flux/llmInterface/llmTools/llmToolsTypes.ts';
import type { ToolDefinition } from 'app-components/flux/llmInterface/llmTypes';
import type { FeatureFlagsKeys } from 'app-components/flux/feature-flags/featureFlagsTypes';
import { safeParse } from 'app-components/flux/llmInterface/llmUtils';
import { getLogger } from 'app-components/utils/core';
import { returnToolResponse } from 'app-components/flux/llmInterface/llmTools/llmToolsFramework';
import { executeNavigationIntent } from 'navigation/intents/executor';
import { getNavigationIntents } from 'navigation/intents/registry';
import 'navigation/intents/registrations';
const log = getLogger('llmWebTools');

// Optional builder args if you want to vary tools by app state later
interface BuildToolsOptions {
  // Example: feature flags, user role, etc.
  enableSpendingTools?: boolean;
  enableCharts?: boolean;
}

type Intent = ReturnType<typeof getNavigationIntents>[number];
type IntentParam = NonNullable<Intent['params']>['definitions'][number];

export type WebChartValueEntry = {
  field: string;
  value: number;
};

export type WebChartValuesRow = Record<string, string | number | WebChartValueEntry[]> & {
  category: string | number;
  values: WebChartValueEntry[];
};

export type WebChartInlineRow = Record<string, string | number>;

export type WebChartDataRow = WebChartValuesRow | WebChartInlineRow;

export type WebChartRequest = {
  chartType: 'line' | 'bar' | 'column' | 'pie' | 'area' | 'sidewaysBar';
  title?: string;
  description?: string;
  categoryField: string;
  valueFields: string[];
  data: WebChartDataRow[];
};

export const WEB_BUILD_CHART_FLAG_KEY: FeatureFlagsKeys = 'aiCharts';

export const webBuildChartTool: ToolHandler = (msg) => {
  const args = safeParse(msg.arguments) as WebChartRequest | null;

  if (!args) {
    return returnToolResponse(msg, {
      success: false,
      error: 'Invalid chart arguments.',
    });
  }

  return returnToolResponse(msg, {
    success: true,
    result: {
      type: 'chart',
      chart: args,
    },
  });
};

const buildParamSchema = (definition: IntentParam): JSONSchemaProperty => {
  if (definition.type === 'string[]') {
    const items: JSONSchemaProperty = { type: 'string' };
    if (definition.enum?.length) {
      items.enum = definition.enum;
    }
    return {
      type: 'array',
      items,
      description: definition.description,
    };
  }

  const schema: JSONSchemaProperty = {
    type: definition.type,
    description: definition.description,
  };
  if (definition.enum?.length) {
    schema.enum = definition.enum;
  }
  return schema;
};

const formatIntentSummary = (intent: Intent): string => {
  const definitions = intent.params?.definitions ?? [];
  if (definitions.length === 0) {
    return `- "${intent.name}": ${intent.description}`;
  }

  const paramsSummary = definitions
    .map((definition) => {
      const enumHint = definition.enum?.length ? ` options: ${definition.enum.join(', ')}` : '';
      const requiredHint = definition.required ? ' required' : '';
      return `${definition.name} (${definition.type}${requiredHint})${enumHint}: ${definition.description}`;
    })
    .join('; ');

  return `- "${intent.name}": ${intent.description} [params: ${paramsSummary}]`;
};

const buildNavigateDescription = (intents: Intent[]): string => {
  const lines = [
    'Use this tool to request navigation in the web app using intent keys.',
    'Provide params only when the selected intent supports them.',
    'Available intents:',
    ...intents.map(formatIntentSummary),
  ];
  return lines.join('\n');
};

const buildParamsSchema = (intents: Intent[]): JSONSchemaProperty => {
  // Flatten all param definitions across intents into a single permissive params object.
  // Intent-specific validation happens in the intent resolver at runtime.
  const definitions = intents.flatMap((intent) => intent.params?.definitions ?? []);

  const properties: Record<string, JSONSchemaProperty> = {};
  for (const definition of definitions) {
    // If multiple intents declare the same param name, keep the first schema encountered.
    if (properties[definition.name]) continue;
    properties[definition.name] = buildParamSchema(definition);
  }

  return {
    type: 'object',
    description: 'Optional parameters for the intent. Available params depend on the intent being navigated to.',
    additionalProperties: false,
    properties,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function buildWebTools(opts: BuildToolsOptions = {}): ToolDefinition[] {
  const tools: ToolDefinition[] = [];
  const enableCharts = opts.enableCharts ?? true;

  // this defaults to true, but shows how you can use opts to control various tool definitions
  if (opts.enableSpendingTools ?? true) {
    log.debug('buildWebTools: enabling web tools with opts', opts);

    /*--------------------------------
     * NAVIGATE
     *------------------------------ */
    const intents = getNavigationIntents();
    const intentNames = intents.map((intent) => intent.name);

    tools.push({
      name: 'web_navigate',
      description: `Navigate the user to a specific feature in the Quicken Simplifi web app. When the user asks to go somewhere or see something, navigate immediately without asking for confirmation.

Some features support optional parameters for filtering/customization. Pass them in the 'params' object.

Available features:${buildNavigateDescription(intents)}`,
      parameters: {
        type: 'object',
        properties: {
          intent: {
            type: 'string',
            enum: intentNames,
            description: 'The navigation intent to execute.',
          },
          params: buildParamsSchema(intents),
        },
        required: ['intent'],
        additionalProperties: false,
      },
    });

    /*--------------------------------
     * BUILD AMCHART
     *------------------------------ */
    if (enableCharts) {
      tools.push({
        name: 'web_build_chart',
        description:
          'MANDATORY CHECK: You MUST evaluate charting whenever your response contains numerical data, comparisons, or trends. ' +
          'Only call this tool if a chart meaningfully improves understanding.\n\n' +
          'HARD REQUIREMENT: If the user asks for a Top-N list (N is at least 3), a breakdown across at least 3 categories, or a time-series with at least 3 periods, you MUST call web_build_chart. ' +
          'Do NOT answer with the numeric list unless you also call web_build_chart (unless the user explicitly says "no chart").\n\n' +
          'CHART WHEN (default):\n\n' +
          '- At least 3 categories or at least 3 time periods.\n' +
          '- Ranked/top-N lists with at least 3 items.\n' +
          '- Distributions/compositions with at least 3 categories.\n\n' +
          'DO NOT chart when:\n\n' +
          '- Showing account lists/balances or account counts.\n' +
          '- Showing transaction lists (recent transactions, last N transactions).\n' +
          '- Showing bills/reminders or due dates.\n' +
          '- Single numeric values.\n' +
          '- Two-item comparisons or trivial arithmetic.\n' +
          '- The user explicitly says "no chart".\n\n' +
          'DATA MAPPING: > - Format: { category: string, values: [{ field: string, value: number }] }.\n\n' +
          "If the user provides numbers, use them. If the user asks a general question that implies data (e.g., 'How tall are the world\\'s mountains?'), provide a representative dataset of 5-10 items.",
        parameters: {
          type: 'object',
          additionalProperties: false,
          properties: {
            chartType: {
              type: 'string',
              enum: ['line', 'bar', 'column', 'pie', 'area', 'sidewaysBar'],
              description: 'Chart type to render.',
            },
            title: { type: 'string', description: 'Title to display above the chart.' },
            description: { type: 'string', description: 'Optional subtitle/description for context.' },
            categoryField: { type: 'string', description: 'Field name to use for x/category axis.' },
            valueFields: {
              type: 'array',
              items: { type: 'string' },
              description: 'One or more field names to plot as series on the chart.',
            },
            data: {
              type: 'array',
              minItems: 2,
              maxItems: 30,
              items: {
                type: 'object',
                additionalProperties: false,
                properties: {
                  category: { type: 'string' },
                  values: {
                    type: 'array',
                    minItems: 1,
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: { type: 'string' },
                        value: { type: 'number' },
                      },
                      required: ['field', 'value'],
                    },
                  },
                },
                required: ['category', 'values'],
              },
              description:
                'Array of data points. Fixed shape rows: { category: string, values: [{ field: string, value: number }] }. Keep it small (<30). Do not send empty objects. If no real data, fabricate plausible sample rows.',
            },
          },
          required: ['chartType', 'categoryField', 'valueFields', 'data'],
        },
      });
    }
  }
  return tools;
}

export const webNavigateTool: ToolHandler = (msg) => {
  const fnArgs = safeParse(msg.arguments) || {};
  const intent =
    typeof fnArgs.intent === 'string' ? fnArgs.intent : typeof fnArgs.feature === 'string' ? fnArgs.feature : '';
  const args = isRecord(fnArgs.params) ? fnArgs.params : isRecord(fnArgs.args) ? fnArgs.args : undefined;

  log.debug('web_navigate called', { intent, args });

  if (!intent) {
    return returnToolResponse(msg, { success: false, error: 'Missing navigation intent.' });
  }

  const result = executeNavigationIntent(intent, args);
  if (result.type === 'error') {
    return returnToolResponse(msg, { success: false, error: result.reason });
  }

  return returnToolResponse(msg, {
    success: true,
    result,
  });
};

// Registry of tool-name → handler
export function buildWebToolHandlers(opts: BuildToolsOptions = {}): Record<string, ToolHandler> {
  const enableCharts = opts.enableCharts ?? true;
  return {
    web_navigate: webNavigateTool,
    ...(enableCharts ? { web_build_chart: webBuildChartTool } : {}),
  };
}
